import { useState, useEffect, useCallback, useRef } from "react";
import { getGtdTickets, getGtdCandidatos, upsertGtdTicket, removeGtdTicket, reordenarGtd } from "../api/gtd.api";
import { GTD_CATEGORIA_COLOR } from "../components/GtdQuickAssign";

const sortColuna = (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || a.id_ticket - b.id_ticket;

const COLUNAS = [
  { key: "Imediatamente", desc: "Precisa de ação agora — risco de atraso hoje." },
  { key: "Urgente",       desc: "Alta prioridade — resolver nos próximos ciclos." },
  { key: "Monitorar",     desc: "Sem ação imediata, mas acompanhar de perto." },
  { key: "Aguardar",      desc: "Depende de terceiros — QA, cliente, outro time." },
  { key: "Algum dia",     desc: "Sem urgência definida no momento." },
  { key: "Finalizados",   desc: "Encerrado — controle interno, não altera o ticket no Redmine." },
];

const REFRESH_SEC = 60;

export default function Gtd() {
  const [tickets,     setTickets]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(false);
  const [dragOverCol,  setDragOverCol]  = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null); // { id_ticket, position: "before"|"after" }
  const [addModalCol, setAddModalCol] = useState(null);
  const [obsModal,    setObsModal]    = useState(null);
  const [countdown,   setCountdown]   = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const data = await getGtdTickets();
      setTickets(data.tickets || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /* Reordena/move um ticket dentro do quadro. `targetId` é o ticket sobre o
     qual foi solto (null = soltar na área vazia da coluna, vai para o fim). */
  async function commitReorder(idTicket, categoria, targetId, position) {
    const arrastado = tickets.find(t => t.id_ticket === idTicket);
    if (!arrastado) return;

    const mudouCategoria = arrastado.categoria !== categoria;

    const colunaSemArrastado = tickets
      .filter(t => t.categoria === categoria && t.id_ticket !== idTicket)
      .sort(sortColuna);

    let index = targetId ? colunaSemArrastado.findIndex(t => t.id_ticket === targetId) : -1;
    if (index === -1) index = colunaSemArrastado.length;
    else if (position === "after") index += 1;

    const novaColuna = [...colunaSemArrastado];
    novaColuna.splice(index, 0, arrastado);

    const ordens = novaColuna.map((t, i) => ({ id_ticket: t.id_ticket, ordem: i }));
    const ordemPorId = Object.fromEntries(ordens.map(o => [o.id_ticket, o.ordem]));

    setTickets(prev => prev.map(t => {
      if (t.id_ticket === idTicket) return { ...t, categoria, ordem: ordemPorId[t.id_ticket] };
      if (ordemPorId[t.id_ticket] !== undefined) return { ...t, ordem: ordemPorId[t.id_ticket] };
      return t;
    }));

    try {
      if (mudouCategoria) await upsertGtdTicket(idTicket, categoria, arrastado.observacao);
      await reordenarGtd(ordens);
    } catch {
      load();
    }
  }

  async function handleRemove(idTicket) {
    if (!window.confirm(`Remover o ticket #${idTicket} do quadro GTD?`)) return;
    setTickets(prev => prev.filter(t => t.id_ticket !== idTicket));
    try {
      await removeGtdTicket(idTicket);
    } catch {
      load();
    }
  }

  async function handleSaveObs(idTicket, categoria, observacao) {
    try {
      const registro = await upsertGtdTicket(idTicket, categoria, observacao.trim() || null);
      setTickets(prev => prev.map(t => t.id_ticket === idTicket ? registro : t));
    } catch {
      alert("Erro ao salvar observação.");
    } finally {
      setObsModal(null);
    }
  }

  async function handleAddTicket(idTicket, categoria) {
    try {
      const registro = await upsertGtdTicket(idTicket, categoria, null);
      setTickets(prev => [...prev.filter(t => t.id_ticket !== idTicket), registro]);
    } catch {
      alert("Erro ao adicionar ticket ao GTD.");
    } finally {
      setAddModalCol(null);
    }
  }

  function handleColumnDrop(e, categoria) {
    e.preventDefault();
    setDragOverCol(null);
    setDragOverCard(null);
    const idTicket = Number(e.dataTransfer.getData("id_ticket"));
    commitReorder(idTicket, categoria, null, null);
  }

  function handleCardDrop(e, categoria, targetTicket) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    const position = dragOverCard?.id_ticket === targetTicket.id_ticket ? dragOverCard.position : "before";
    setDragOverCard(null);
    const idTicket = Number(e.dataTransfer.getData("id_ticket"));
    if (idTicket === targetTicket.id_ticket) return;
    commitReorder(idTicket, categoria, targetTicket.id_ticket, position);
  }

  function handleCardDragOver(e, ticket) {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const position = (e.clientY - rect.top) > rect.height / 2 ? "after" : "before";
    setDragOverCard(prev =>
      prev?.id_ticket === ticket.id_ticket && prev?.position === position
        ? prev
        : { id_ticket: ticket.id_ticket, position }
    );
  }

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", fontSize: 13, padding: 40, textAlign: "center" }}>Carregando quadro GTD…</div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", marginTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={load} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  return (
    <div style={page}>

      <header style={header}>
        <div>
          <h1 style={h1}>Quadro GTD</h1>
          <div style={sub}>Organize o que precisa de atenção agora, o que pode esperar e o que está em monitoramento.</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "#475569" }}>Atualiza em {countdown}s</span>
          <button onClick={() => { loadRef.current(); setCountdown(REFRESH_SEC); }} title="Atualizar agora" style={btnRefresh}>↻</button>
        </div>
      </header>

      <div style={board}>
        {COLUNAS.map(col => {
          const cor = GTD_CATEGORIA_COLOR[col.key];
          const colTickets = tickets.filter(t => t.categoria === col.key).sort(sortColuna);
          const isDragOver = dragOverCol === col.key;

          return (
            <div
              key={col.key}
              style={column}
              onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
              onDrop={e => handleColumnDrop(e, col.key)}
            >
              <div style={colHead(cor, isDragOver)}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: cor, boxShadow: `0 0 6px ${cor}88`, flexShrink: 0 }} />
                <span style={{ ...colHeadLabel, color: cor }}>{col.key}</span>
                <span style={colBadge(cor)}>{colTickets.length}</span>
              </div>
              <div style={colDesc}>{col.desc}</div>

              <div style={colBody}>
                {colTickets.map(t => (
                  <GtdCard
                    key={t.id_ticket}
                    ticket={t}
                    cor={cor}
                    onRemove={() => handleRemove(t.id_ticket)}
                    onEditObs={() => setObsModal(t)}
                    dropIndicator={dragOverCard?.id_ticket === t.id_ticket ? dragOverCard.position : null}
                    onDragOverCard={e => handleCardDragOver(e, t)}
                    onDropCard={e => handleCardDrop(e, col.key, t)}
                  />
                ))}
                {colTickets.length === 0 && (
                  <div style={isDragOver ? emptyMsgDrop : emptyMsg}>
                    {isDragOver ? "↓ Soltar aqui" : "Nenhum ticket"}
                  </div>
                )}
              </div>

              <ColAddButton cor={cor} onClick={() => setAddModalCol(col.key)} />
            </div>
          );
        })}
      </div>

      {addModalCol && (
        <AddTicketModal
          categoria={addModalCol}
          jaAdicionados={tickets.map(t => t.id_ticket)}
          onAdd={handleAddTicket}
          onClose={() => setAddModalCol(null)}
        />
      )}

      {obsModal && (
        <ObsModal
          ticket={obsModal}
          onSave={handleSaveObs}
          onClose={() => setObsModal(null)}
        />
      )}

    </div>
  );
}

/* ─── Card ────────────────────────────────────────────────────────── */

function GtdCard({ ticket, cor, onRemove, onEditObs, dropIndicator, onDragOverCard, onDropCard }) {
  const [hovered, setHovered] = useState(false);

  const indicatorShadow =
    dropIndicator === "before" ? "inset 0 2px 0 0 #60a5fa" :
    dropIndicator === "after"  ? "inset 0 -2px 0 0 #60a5fa" :
    null;
  const hoverShadow = hovered ? "0 4px 16px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(96,165,250,0.12)" : "none";

  return (
    <div
      style={{
        ...card,
        borderLeft: `3px solid ${cor}`,
        boxShadow: indicatorShadow ? `${indicatorShadow}, ${hoverShadow}` : hoverShadow,
      }}
      draggable
      onDragStart={e => { e.dataTransfer.setData("id_ticket", ticket.id_ticket); e.dataTransfer.effectAllowed = "move"; }}
      onDragOver={onDragOverCard}
      onDrop={onDropCard}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <a
          href={`https://redmine.lexsis.com.br/issues/${ticket.id_ticket}`}
          target="_blank" rel="noreferrer"
          style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 700, fontSize: 12 }}
        >
          #{ticket.id_ticket}
        </a>
        {ticket.status && <span style={statusChipStyle(ticket.status)}>{ticket.status}</span>}
        <button
          style={{ ...iconBtn("#f87171"), marginLeft: "auto", opacity: hovered ? 1 : 0, transition: "opacity 0.18s" }}
          onClick={onRemove}
          title="Remover do quadro"
        >✕</button>
      </div>

      {ticket.subject && <div style={cardDesc}>{ticket.subject}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {ticket.projeto && <span style={metaTag}>📁 {ticket.projeto}</span>}
        {ticket.desenvolvedor && <span style={metaTag}>👤 {ticket.desenvolvedor}</span>}
        {ticket.responsavel && <span style={metaTag}>🧭 {ticket.responsavel}</span>}
      </div>

      {ticket.observacao && <div style={obsBox}>{ticket.observacao}</div>}

      <button style={editObsBtn} onClick={onEditObs}>
        {ticket.observacao ? "Editar observação" : "+ observação"}
      </button>
    </div>
  );
}

function ColAddButton({ onClick, cor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      style={{
        margin: "0 10px 10px",
        padding: "7px 12px",
        borderRadius: 8,
        border: `1.5px dashed ${hovered ? cor + "60" : "rgba(96,165,250,0.15)"}`,
        background: hovered ? `${cor}08` : "transparent",
        color: hovered ? cor : "#334155",
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        transition: "all 0.15s",
        width: "calc(100% - 20px)",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 400 }}>+</span>
      Adicionar ticket
    </button>
  );
}

/* ─── AddTicketModal ─────────────────────────────────────────────── */

function AddTicketModal({ categoria, jaAdicionados, onAdd, onClose }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca,   setBusca]   = useState("");

  useEffect(() => {
    getGtdCandidatos()
      .then(data => setTickets(Array.isArray(data) ? data : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const disponiveis = tickets
    .filter(t => !jaAdicionados.includes(t.id_ticket))
    .filter(t => {
      if (!busca.trim()) return true;
      const q = busca.toLowerCase();
      return String(t.id_ticket).includes(q) || (t.subject || "").toLowerCase().includes(q);
    });

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalBox}>
        <div style={modalHeader}>
          <span style={modalTitle}>Adicionar ticket — {categoria}</span>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "14px 22px 0" }}>
          <input
            style={fieldInput}
            placeholder="Buscar por número ou assunto..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            autoFocus
          />
        </div>
        <div style={modalBody}>
          {loading && <div style={{ color: "#475569", fontSize: 12 }}>Carregando tickets...</div>}
          {!loading && disponiveis.length === 0 && (
            <div style={{ color: "#334155", fontSize: 12, fontStyle: "italic" }}>Nenhum ticket disponível.</div>
          )}
          {!loading && disponiveis.map(t => (
            <div key={t.id_ticket} style={pickItem} onClick={() => onAdd(t.id_ticket, categoria)}>
              <span style={{ color: "#60a5fa", fontWeight: 700, fontSize: 12 }}>#{t.id_ticket}</span>
              <span style={{ color: "#e2e8f0", fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.subject}
              </span>
              {t.status && <span style={statusChipStyle(t.status)}>{t.status}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── ObsModal ────────────────────────────────────────────────────── */

function ObsModal({ ticket, onSave, onClose }) {
  const [observacao, setObservacao] = useState(ticket.observacao || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalBox, maxWidth: 420 }}>
        <div style={modalHeader}>
          <span style={modalTitle}>Observação — #{ticket.id_ticket}</span>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={modalBody}>
          <textarea
            style={{ ...fieldInput, height: 100, resize: "vertical" }}
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            placeholder="Anote o motivo, contexto ou próximo passo..."
            autoFocus
          />
        </div>
        <div style={modalFooter}>
          <button style={btnCancel} onClick={onClose}>Cancelar</button>
          <button
            style={{ ...btnSave, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
            onClick={async () => { setSaving(true); await onSave(ticket.id_ticket, ticket.categoria, observacao); setSaving(false); }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9", fontFamily: "'Inter', sans-serif" };

const header = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap" };

const h1  = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };
const sub = { fontSize: 12, color: "#475569", marginTop: 4 };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const board = { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 14, alignItems: "start" };

const column = {
  background: "rgba(11,22,42,0.5)", border: "1px solid rgba(96,165,250,0.08)",
  borderRadius: 14, display: "flex", flexDirection: "column", transition: "box-shadow 0.2s",
  minHeight: 200,
};

const colHead = (cor, dragOver) => ({
  padding: "12px 14px", display: "flex", alignItems: "center", gap: 8,
  borderBottom: `2px solid ${dragOver ? cor : cor + "40"}`,
  background: dragOver ? `${cor}22` : `${cor}0a`,
  borderRadius: "14px 14px 0 0", transition: "background 0.15s",
});

const colHeadLabel = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, flex: 1 };

const colBadge = cor => ({
  background: `${cor}22`, color: cor, border: `1px solid ${cor}44`,
  borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700,
});

const colDesc = { fontSize: 10, color: "#475569", padding: "8px 14px 0", lineHeight: 1.4 };

const colBody = { display: "flex", flexDirection: "column", gap: 8, padding: "10px 10px 4px" };

const emptyMsg = { color: "#2d3a4a", fontSize: 11, textAlign: "center", padding: "16px 0", fontStyle: "italic" };

const emptyMsgDrop = {
  color: "#60a5fa", fontSize: 12, fontWeight: 600, textAlign: "center", padding: "16px 0",
  border: "1px dashed rgba(96,165,250,0.4)", borderRadius: 8, background: "rgba(96,165,250,0.04)",
};

const card = {
  background: "rgba(11,22,42,0.72)", border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 10, padding: "11px 11px 10px", display: "flex", flexDirection: "column", gap: 7,
  backdropFilter: "blur(8px)", cursor: "grab", transition: "box-shadow 0.15s",
};

const cardDesc = { color: "#e2e8f0", fontSize: 12, fontWeight: 500, lineHeight: 1.4 };

const metaTag = {
  fontSize: 10, fontWeight: 600, color: "#94a3b8", background: "rgba(148,163,184,0.08)",
  border: "1px solid rgba(148,163,184,0.18)", borderRadius: 4, padding: "2px 6px",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
};

const statusChip = {
  fontSize: 9, color: "#64748b", background: "rgba(71,85,105,0.2)", padding: "2px 6px",
  borderRadius: 4, whiteSpace: "nowrap",
};

/* Status que merecem destaque no card — sinalizam ticket travado/em retrabalho */
const STATUS_HIGHLIGHT_COLOR = {
  "Parado":      "#fb923c",
  "Retrabalhar": "#f97316",
};

const statusChipStyle = (status) => {
  const color = STATUS_HIGHLIGHT_COLOR[status];
  if (!color) return statusChip;
  return {
    fontSize: 9, fontWeight: 700, color, background: `${color}26`,
    border: `1px solid ${color}66`, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap",
  };
};

const obsBox = {
  fontSize: 11, color: "#94a3b8", background: "rgba(96,165,250,0.05)",
  border: "1px solid rgba(96,165,250,0.1)", borderRadius: 6, padding: "6px 8px", lineHeight: 1.4,
};

const editObsBtn = {
  alignSelf: "flex-start", background: "none", border: "none", color: "#334155",
  fontSize: 10, fontWeight: 600, cursor: "pointer", padding: 0,
};

const iconBtn = cor => ({
  width: 22, height: 22, borderRadius: 6, border: `1px solid ${cor}33`, background: "transparent",
  color: cor, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
});

const backdrop = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
};

const modalBox = {
  background: "#08122a", border: "1px solid rgba(96,165,250,0.15)", borderRadius: 16,
  width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", maxHeight: "80vh", overflow: "hidden",
};

const modalHeader = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "18px 22px 14px", borderBottom: "1px solid rgba(96,165,250,0.1)",
};

const modalTitle    = { color: "#f1f5f9", fontWeight: 700, fontSize: 15 };
const modalCloseBtn = { background: "transparent", border: "none", color: "#475569", fontSize: 16, cursor: "pointer" };
const modalBody     = { padding: "14px 22px 18px", display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" };
const modalFooter   = { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 22px", borderTop: "1px solid rgba(96,165,250,0.1)" };

const fieldInput = {
  width: "100%", padding: "8px 11px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.15)",
  background: "rgba(11,22,42,0.7)", color: "#e2e8f0", fontSize: 13, outline: "none",
  boxSizing: "border-box", colorScheme: "dark", fontFamily: "inherit",
};

const pickItem = {
  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8,
  cursor: "pointer", border: "1px solid transparent",
};

const btnCancel = { padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(96,165,250,0.15)", background: "transparent", color: "#475569", fontSize: 13, cursor: "pointer" };
const btnSave   = { padding: "9px 20px", borderRadius: 9, border: "none", background: "linear-gradient(135deg, #3b82f6, #06b6d4)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" };
